import React from 'react';
import { Link } from 'react-router-dom';
import PublicImage from '../PublicImage';
import { cartLineKey } from '../cart/cartUtils';
import { formatCharCustomizationLabel, normalizePersonalizationLetter } from '../productPersonalization';
import { formatINR } from './formatINR';

function shippingLabel(shipping, loading) {
  if (loading) return '…';
  if (shipping > 0) return formatINR(shipping);
  return 'Free';
}

export default function OrderSummaryPanel({
  lines,
  subtotal,
  discount = 0,
  couponCode = '',
  freeShippingFromCoupon = false,
  shipping = 0,
  shippingCaption = '',
  shippingLoading = false,
  showThumbnails = true,
  editCartHref = '/cart',
  primaryAction,
  footnote,
  deliveryNote,
}) {
  const total = Math.max(0, subtotal - discount) + (shippingLoading ? 0 : shipping);
  const itemCount = lines.reduce((s, l) => s + l.qty, 0);

  return (
    <aside className="order-summary-panel">
      <div className="order-summary-panel__head">
        <h2 className="order-summary-panel__title">Order summary</h2>
        <span className="order-summary-panel__count">
          {itemCount} item{itemCount === 1 ? '' : 's'}
        </span>
      </div>

      {deliveryNote ? <p className="order-summary-panel__delivery-note small mb-3">{deliveryNote}</p> : null}

      <ul className="order-summary-panel__lines list-unstyled">
        {lines.map((line) => (
          <li
            key={cartLineKey(line.categorySlug, line.productId, line.customization)}
            className="order-summary-panel__line"
          >
            {showThumbnails ? (
              <div className="order-summary-panel__thumb">
                {line.image ? (
                  <PublicImage src={line.image} alt="" />
                ) : (
                  <span className="order-summary-panel__thumb-placeholder" aria-hidden />
                )}
                <span className="order-summary-panel__qty-badge">{line.qty}</span>
              </div>
            ) : null}
            <div className="order-summary-panel__line-body">
              <span className="order-summary-panel__name">{line.name}</span>
              {normalizePersonalizationLetter(line.customization) ? (
                <span className="order-summary-panel__meta d-block">
                  {formatCharCustomizationLabel(line.customization)}
                </span>
              ) : null}
              {!showThumbnails ? <span className="order-summary-panel__meta">Qty {line.qty}</span> : null}
            </div>
            <span className="order-summary-panel__line-price">{formatINR(line.price * line.qty)}</span>
          </li>
        ))}
      </ul>

      <div className="order-summary-panel__rows">
        <div className="order-summary-panel__row">
          <span>Subtotal</span>
          <span>{formatINR(subtotal)}</span>
        </div>
        {discount > 0 || freeShippingFromCoupon ? (
          <div className="order-summary-panel__row order-summary-panel__row--discount">
            <span>
              {couponCode ? (
                <>
                  Promo <span className="order-summary-panel__coupon-tag">{couponCode}</span>
                </>
              ) : (
                'Promo'
              )}
            </span>
            <span className="order-summary-panel__discount-val">
              {discount > 0 ? `−${formatINR(discount)}` : '—'}
              {freeShippingFromCoupon ? (
                <span className="order-summary-panel__free-ship-note"> + free delivery</span>
              ) : null}
            </span>
          </div>
        ) : null}
        <div className="order-summary-panel__row">
          <span>{shippingCaption ? `Delivery — ${shippingCaption}` : 'Delivery'}</span>
          <span>{shippingLabel(shipping, shippingLoading)}</span>
        </div>
        <div className="order-summary-panel__row order-summary-panel__row--total">
          <span>Total</span>
          <span>{shippingLoading ? formatINR(subtotal) : formatINR(total)}</span>
        </div>
      </div>

      {primaryAction ? <div className="order-summary-panel__action">{primaryAction}</div> : null}

      {editCartHref ? (
        <Link to={editCartHref} className="order-summary-panel__edit">
          Edit bag
        </Link>
      ) : null}

      {footnote ? <p className="order-summary-panel__footnote small text-muted mb-0">{footnote}</p> : null}
    </aside>
  );
}
